/**
 * Primary hook for managing financial data in the CashFlow Statement module.
 *
 * Data lifecycle:
 *   1. On mount, instantly render from localStorage cache (so the page never flashes empty).
 *   2. Fire parallel API fetches; when they return, replace state with server data and refresh cache.
 *   3. If both API and cache are empty, fall back to demo data so the UI is never blank.
 *   4. Writes go to the API first; local state + cache update on success.
 *
 * localStorage is a *cache only* once the API is reachable. The server is the source of truth.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  FinancialData,
  MonthlySummary,
  Asset,
  Liability,
  QuickAddEntry,
  MonthAnnotation,
} from '../types/cashflow';
import { demoSummaries, demoAssets, demoLiabilities } from '../data/cashflowDemo';
import { deriveAllMetrics } from '../utils/cashflowCalc';
import {
  fetchCashflowSummaries,
  fetchCashflowAssets,
  fetchCashflowLiabilities,
  fetchCashflowQuickAdds,
  fetchCashflowAnnotations,
  postCashflowQuickAdd,
  putCashflowAnnotation,
  postCashflowBulk,
} from '../utils/api';

const STORAGE_KEY = 'homebase_cashflow_v2';

interface CachedData extends Partial<FinancialData> {
  isDemo?: boolean;
}

function loadFromStorage(): CachedData {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

function saveToStorage(data: CachedData) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // storage unavailable; ignore
  }
}

export function useFinancialData() {
  const stored = useMemo(() => loadFromStorage(), []);
  const hadCache = Boolean(stored.summaries);

  const [summaries, setSummaries] = useState<MonthlySummary[]>(
    stored.summaries ?? demoSummaries,
  );
  const [assets, setAssets] = useState<Asset[]>(stored.assets ?? demoAssets);
  const [liabilities, setLiabilities] = useState<Liability[]>(
    stored.liabilities ?? demoLiabilities,
  );
  const [quickAdds, setQuickAdds] = useState<QuickAddEntry[]>(stored.quickAdds ?? []);
  const [annotations, setAnnotations] = useState<MonthAnnotation[]>(stored.annotations ?? []);
  const [isDemo, setIsDemo] = useState<boolean>(stored.isDemo ?? !hadCache);
  const [loading, setLoading] = useState<boolean>(!hadCache);
  const [syncError, setSyncError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    try {
      const [s, a, l, q, n] = await Promise.all([
        fetchCashflowSummaries(),
        fetchCashflowAssets(),
        fetchCashflowLiabilities(),
        fetchCashflowQuickAdds(),
        fetchCashflowAnnotations(),
      ]);

      const serverEmpty = s.length === 0 && a.length === 0 && l.length === 0;
      const cacheNow = loadFromStorage();
      const hadCacheNow = Boolean(cacheNow.summaries);

      if (serverEmpty && !hadCacheNow) {
        setIsDemo(true);
        saveToStorage({
          summaries: demoSummaries,
          assets: demoAssets,
          liabilities: demoLiabilities,
          quickAdds: [],
          annotations: [],
          isDemo: true,
        });
      } else {
        setSummaries(s);
        setAssets(a);
        setLiabilities(l);
        setQuickAdds(q);
        setAnnotations(n);
        setIsDemo(false);
        saveToStorage({
          summaries: s,
          assets: a,
          liabilities: l,
          quickAdds: q,
          annotations: n,
          isDemo: false,
        });
      }
      setSyncError(null);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Failed to load data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    refetch().catch(() => {
      // Already captured in syncError; keep cache/demo on screen
    }).finally(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [refetch]);

  const sortedSummaries = useMemo(
    () => [...summaries].sort((a, b) => a.month.localeCompare(b.month)),
    [summaries],
  );

  const allMetrics = useMemo(
    () => deriveAllMetrics(sortedSummaries, assets, liabilities),
    [sortedSummaries, assets, liabilities],
  );

  const latestMetrics = allMetrics[allMetrics.length - 1];

  async function loadCSVData(
    newSummaries: MonthlySummary[],
    newAssets: Asset[],
    newLiabilities: Liability[],
  ) {
    // Optimistic local update
    setSummaries(newSummaries);
    setAssets(newAssets);
    setLiabilities(newLiabilities);
    setIsDemo(false);

    try {
      await postCashflowBulk({
        source: 'csv',
        summaries: newSummaries,
        assets: newAssets,
        liabilities: newLiabilities,
        replace: { summaries: true, assets: true, liabilities: true },
      });
      saveToStorage({
        summaries: newSummaries,
        assets: newAssets,
        liabilities: newLiabilities,
        quickAdds,
        annotations,
        isDemo: false,
      });
      setSyncError(null);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'CSV upload failed to persist');
    }
  }

  async function addQuickEntry(entry: QuickAddEntry) {
    try {
      const saved = await postCashflowQuickAdd(entry);
      const updated = [...quickAdds, saved];
      setQuickAdds(updated);
      saveToStorage({ summaries, assets, liabilities, quickAdds: updated, annotations, isDemo });
      setSyncError(null);

      // Asset/liability quick-adds also write a snapshot row server-side -
      // refetch so the AssetPanel / LiabilityPanel pick it up immediately.
      if (saved.type === 'asset' || saved.type === 'liability') {
        await refetch().catch(() => { /* error already surfaced in syncError */ });
      }
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Quick-add failed to persist');
    }
  }

  async function addAnnotation(annotation: MonthAnnotation) {
    try {
      const saved = await putCashflowAnnotation(annotation.month, annotation.note);
      const filtered = annotations.filter((a) => a.month !== saved.month);
      const updated = [...filtered, saved];
      setAnnotations(updated);
      saveToStorage({ summaries, assets, liabilities, quickAdds, annotations: updated, isDemo });
      setSyncError(null);
    } catch (err) {
      setSyncError(err instanceof Error ? err.message : 'Annotation failed to persist');
    }
  }

  function getMonthAnnotation(month: string): string | undefined {
    return annotations.find((a) => a.month === month)?.note;
  }

  function getAssetsForMonth(month: string): Asset[] {
    return assets.filter((a) => a.month === month);
  }

  function getLiabilitiesForMonth(month: string): Liability[] {
    return liabilities.filter((l) => l.month === month);
  }

  return {
    summaries: sortedSummaries,
    assets,
    liabilities,
    quickAdds,
    annotations,
    allMetrics,
    latestMetrics,
    isDemo,
    loading,
    syncError,
    loadCSVData,
    addQuickEntry,
    addAnnotation,
    getMonthAnnotation,
    getAssetsForMonth,
    getLiabilitiesForMonth,
    refetch,
  };
}
